export const jupiterTree = {
  id: 'jupiter',
  label: 'Jupiter',
  type: 'Gas giant',
  summary: 'The largest planet: a striped gas giant with 115 known moons, a giant storm, and its own mini solar system.',
  payload: [
    {
      type: 'description',
      text: 'Jupiter is so massive that if you doubled it, it would still not become a star. It has 2.5× the mass of every other planet in the solar system combined. Its visible "surface" is cloud tops of ammonia; below, hydrogen transitions through increasingly exotic states, eventually becoming metallic hydrogen that conducts electricity and powers the strongest planetary magnetic field in the solar system.'
    },
    {
      type: 'stats',
      label: 'At a glance',
      entries: [
        { label: 'Diameter',          value: '139,820 km (10.97 × Earth)' },
        { label: 'Mass',              value: '1.898 × 10²⁷ kg (318 × Earth)' },
        { label: 'Distance from Sun', value: '778.5 M km (5.20 AU)' },
        { label: 'Orbital period',    value: '11.86 Earth years' },
        { label: 'Day length',        value: '9h 56m (fastest rotator)' },
        { label: 'Moons',             value: '115 confirmed (JPL, October 2026)' },
        { label: 'Rings',             value: 'Yes, faint and dusty' },
        { label: 'Temp at 1 bar',     value: '−108 °C' }
      ]
    }
  ],
  children: [
    {
      id: 'vital-stats',
      label: 'Vital statistics',
      summary: 'Size, gravity, rotation, magnetic field',
      payload: [
        {
          type: 'stats',
          label: 'Physical',
          entries: [
            { label: 'Equatorial radius', value: '71,492 km' },
            { label: 'Mean density',      value: '1.326 g/cm³' },
            { label: 'Surface gravity',   value: '25.92 m/s² (2.64 g)' },
            { label: 'Escape velocity',   value: '59.5 km/s' },
            { label: 'Oblateness',        value: '0.0649 (noticeable bulge)' }
          ]
        },
        {
          type: 'stats',
          label: 'Magnetic',
          entries: [
            { label: 'Magnetic moment', value: '~20,000 × Earth\'s (field at cloud tops ~14×)' },
            { label: 'Magnetosphere',  value: 'Extends past Saturn\'s orbit' },
            { label: 'Radiation belts', value: 'Lethal to unshielded electronics' },
            { label: 'Source',         value: 'Metallic hydrogen dynamo' }
          ]
        },
        {
          type: 'callout',
          tone: 'warn',
          text: 'Jupiter\'s radiation environment is so intense that Juno orbits in a highly elliptical polar path to minimize exposure. Galileo, which orbited closer, received a total dose that would have killed a human thousands of times over.'
        }
      ]
    },
    {
      id: 'moons',
      label: 'Major moons',
      summary: 'The four Galileans, each a world in its own right',
      children: [
        {
          id: 'io',
          label: 'Io',
          summary: 'Most volcanically active body in the solar system',
          payload: [
            { type: 'description', text: 'Tidal heating from Jupiter\'s gravity (and from its sister moons Europa and Ganymede) keeps Io\'s interior molten. Hundreds of active volcanoes, some throwing plumes 500 km into space, constantly resurface it in sulfur and sulfur dioxide frost.' },
            {
              type: 'stats',
              entries: [
                { label: 'Diameter',      value: '3,643 km (slightly larger than Moon)' },
                { label: 'Orbital period', value: '1.77 Earth days' },
                { label: 'Active volcanoes', value: '400+ confirmed' },
                { label: 'Surface',       value: 'Sulfur and SO₂ frost' }
              ]
            }
          ]
        },
        {
          id: 'europa',
          label: 'Europa',
          summary: 'Icy shell over a global ocean, a prime target for life',
          payload: [
            { type: 'description', text: 'Beneath a 15–25 km thick shell of water ice lies a global liquid-water ocean likely twice the volume of Earth\'s oceans. Possible hydrothermal activity at the rocky seafloor, plus the chemistry hinted at by probable plumes and surface deposits, make Europa one of the best places in the solar system to look for life.' },
            {
              type: 'stats',
              entries: [
                { label: 'Diameter',       value: '3,122 km' },
                { label: 'Orbital period', value: '3.55 Earth days' },
                { label: 'Ice shell',      value: '15 – 25 km thick' },
                { label: 'Subsurface ocean', value: '60 – 150 km deep' }
              ]
            },
            { type: 'callout', tone: 'info', text: 'NASA\'s Europa Clipper launched October 2024 and arrives 2030. It will do ~49 close flybys to map the ice shell, sample plumes, and characterize habitability.' }
          ]
        },
        {
          id: 'ganymede',
          label: 'Ganymede',
          summary: 'Largest moon in the solar system, with its own magnetic field',
          payload: [
            { type: 'description', text: 'Bigger than Mercury. Uniquely, Ganymede generates its own magnetic field, likely from a liquid iron core. Its surface is a split of ancient dark cratered terrain and younger grooved ice terrain. A salty subsurface ocean may hold more water than all of Earth.' },
            {
              type: 'stats',
              entries: [
                { label: 'Diameter',       value: '5,262 km (larger than Mercury)' },
                { label: 'Orbital period', value: '7.15 Earth days' },
                { label: 'Magnetic field', value: 'Yes, the only moon known to have one' }
              ]
            }
          ]
        },
        {
          id: 'callisto',
          label: 'Callisto',
          summary: 'Oldest, most cratered surface in the solar system',
          payload: [
            { type: 'description', text: 'The outermost Galilean moon. Its surface is saturated with impact craters, one of the oldest landscapes known. May also hide a subsurface ocean. Outside the worst of Jupiter\'s radiation belt, it has been proposed as a human outpost.' },
            {
              type: 'stats',
              entries: [
                { label: 'Diameter',       value: '4,821 km' },
                { label: 'Orbital period', value: '16.7 Earth days' },
                { label: 'Surface age',    value: '~4 billion years' }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'atmosphere',
      label: 'Atmosphere',
      summary: 'Ammonia clouds, banded jets, and the Great Red Spot',
      payload: [
        {
          type: 'description',
          text: 'Jupiter is effectively all atmosphere: it has no solid surface. The visible part is stratified into alternating light zones and darker belts. Winds between them reach about 540 km/h (150 m/s) near the equator. Below, pressure and temperature rise steadily until hydrogen transitions to a liquid and then to a metallic state near the planet\'s center.'
        },
        {
          type: 'composition-bars',
          entries: [
            { name: 'Hydrogen (H₂)',  percent: 89.8, color: '#88ccff' },
            { name: 'Helium (He)',    percent: 10.2, color: '#ffaaaa' },
            { name: 'Methane (CH₄)',  percent: 0.3,  color: '#aa6644' },
            { name: 'Ammonia (NH₃)',  percent: 0.026, color: '#c099ff' },
            { name: 'Hydrogen deuteride (HD)', percent: 0.003, color: '#ffd966' }
          ]
        },
        {
          type: 'stats',
          label: 'The Great Red Spot',
          entries: [
            { label: 'Size',             value: '~14,000 × 12,000 km (shrinking)' },
            { label: 'Observed since',   value: 'At least 1830 (possibly 1665)' },
            { label: 'Wind speed',       value: '430 km/h at edges' },
            { label: 'Height',           value: '8 km above surrounding clouds' }
          ]
        }
      ]
    },
    {
      id: 'rings',
      label: 'Rings',
      summary: 'Faint, dusty, discovered by Voyager 1',
      payload: [
        {
          type: 'description',
          text: 'Jupiter\'s rings were discovered in 1979 and are much fainter than Saturn\'s. They are made of fine dust from small moons (Adrastea, Metis, Amalthea, Thebe) being eroded by micrometeorite impacts.'
        },
        {
          type: 'stats',
          entries: [
            { label: 'Halo ring',      value: '92,000 – 122,500 km from center' },
            { label: 'Main ring',      value: '122,500 – 129,000 km' },
            { label: 'Amalthea gossamer', value: '129,000 – 182,000 km' },
            { label: 'Thebe gossamer', value: '129,000 – 226,000 km' }
          ]
        }
      ]
    },
    {
      id: 'missions',
      label: 'Missions & exploration',
      summary: 'From Pioneer flybys to Juno and Europa Clipper',
      payload: [
        {
          type: 'stats',
          entries: [
            { label: 'Pioneer 10/11', value: '1973/1974, first flybys' },
            { label: 'Voyager 1/2',   value: '1979: rings discovered, moons mapped' },
            { label: 'Galileo',       value: '1995–2003, first orbiter + probe' },
            { label: 'Cassini',       value: '2000 flyby, en route to Saturn' },
            { label: 'New Horizons',  value: '2007 flyby, en route to Pluto' },
            { label: 'Juno',          value: 'Since 2016, polar orbit' },
            { label: 'JUICE',         value: 'ESA, launched 2023, arrives 2031' },
            { label: 'Europa Clipper', value: 'NASA, launched 2024, arrives 2030' }
          ]
        }
      ]
    },
    {
      id: 'references',
      label: 'External references',
      summary: 'Where to dig deeper',
      payload: [
        {
          type: 'links',
          entries: [
            { label: 'NASA fact sheet',    url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/jupiterfact.html' },
            { label: 'Juno mission',       url: 'https://www.nasa.gov/mission/juno/' },
            { label: 'Europa Clipper',     url: 'https://europa.nasa.gov/' },
            { label: 'NASA: Jupiter facts', url: 'https://science.nasa.gov/jupiter/jupiter-facts/' },
            { label: 'JPL: satellite discovery list', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
            { label: 'Wikipedia: Jupiter', url: 'https://en.wikipedia.org/wiki/Jupiter' }
          ]
        }
      ]
    }
  ]
};
